'use client';

import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { getTokenForApp } from '@/lib/CookieUtils';
import { rememberEngagementMid, trackEngagement } from '@/lib/engagement';
import { useEffect, useState } from 'react';

const DISMISS_KEY = 'hzc_web_push_dismiss';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

async function registerWebPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  const reg = await navigator.serviceWorker.register('/hzc-engagement-sw.js');
  const vapidRes = await fetch(`${getApiBaseUrl()}/e/web-push/vapid`);
  const vapid = await vapidRes.json();
  const publicKey = vapid?.data?.publicKey || vapid?.data?.data?.publicKey;
  if (!publicKey) return;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));
  const token = getTokenForApp();
  await fetch(`${getApiBaseUrl()}/e/web-push/subscribe`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(token ? { token } : {}),
    },
    body: JSON.stringify(sub.toJSON()),
  });
}

export default function EngagementCapture() {
  const [askPush, setAskPush] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mid = params.get('mid');
    const qr = params.get('qr');
    const ad = params.get('ad');

    if (mid) {
      rememberEngagementMid(mid);
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      const token = getTokenForApp();
      if (token) headers.token = token;
      fetch(`${getApiBaseUrl()}/engagement/attach`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ messageId: mid }),
        keepalive: true,
      }).catch(() => {});
    }
    if (qr) {
      trackEngagement({ channel: 'qr', event: 'scanned', campaignId: qr }).catch(() => {});
    }
    if (ad) {
      trackEngagement({ channel: 'ad', event: 'clicked', campaignId: ad }).catch(() => {});
    }

    if (typeof Notification === 'undefined') return undefined;

    const syncSubscription = () => {
      if (Notification.permission === 'granted') {
        registerWebPush().catch(() => {});
      }
    };

    // Keep subscription linked after login (token appears later).
    syncSubscription();
    window.addEventListener('focus', syncSubscription);

    if (Notification.permission === 'granted') {
      return () => window.removeEventListener('focus', syncSubscription);
    }
    if (Notification.permission !== 'default') {
      return () => window.removeEventListener('focus', syncSubscription);
    }
    try {
      if (localStorage.getItem(DISMISS_KEY)) {
        return () => window.removeEventListener('focus', syncSubscription);
      }
    } catch (e) {
      return () => window.removeEventListener('focus', syncSubscription);
    }
    setAskPush(true);
    return () => window.removeEventListener('focus', syncSubscription);
  }, []);

  const enablePush = async () => {
    const permission = await Notification.requestPermission();
    setAskPush(false);
    if (permission !== 'granted') return;
    await registerWebPush().catch(() => {});
  };

  const dismissPush = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      // private mode
    }
    setAskPush(false);
  };

  if (!askPush) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 40,
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px',
        borderRadius: 12,
        background: '#fff',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        maxWidth: 420,
        margin: '0 auto',
      }}
    >
      <span style={{ fontSize: 14 }}>Sipariş ve kampanya bildirimlerini bu tarayıcıda aç.</span>
      <button type="button" onClick={dismissPush} style={{ background: 'transparent', border: 0 }}>
        Sonra
      </button>
      <button type="button" onClick={enablePush}>
        Aç
      </button>
    </div>
  );
}
